import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FORM_SUBMIT_URL } from '../../data/pathsConfig';

// ═══════════════════════════════════════════════════════════════════
// CONVERSATIONAL FORM - Dynamic form with conversational UI
// ═══════════════════════════════════════════════════════════════════

const ConversationalForm = ({ path, onClose }) => {
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const formRef = useRef(null);

  // Scroll to form when it appears
  useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentFieldIndex]);

  const currentField = path.formFields[currentFieldIndex];
  const progress = ((currentFieldIndex + 1) / (path.formFields.length + 2)) * 100; // +2 for name and email

  const handleFieldSubmit = (value) => {
    setFormData(prev => ({ ...prev, [currentField.id]: value }));

    if (currentFieldIndex < path.formFields.length - 1) {
      setCurrentFieldIndex(prev => prev + 1);
    } else {
      // All path-specific fields complete, now ask for contact info
      setCurrentFieldIndex('contact');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    // Prepare form data
    const submissionData = {
      _subject: `New ${path.title} inquiry`,
      _captcha: 'false',
      path: path.id,
      name,
      email,
      ...formData,
      timestamp: new Date().toISOString()
    };

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData)
      });

      if (response.ok) {
        setIsComplete(true);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // In production, show error message to user
      alert('There was an error submitting your request. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isComplete) {
    return (
      <motion.div
        ref={formRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto p-8 bg-white/10 backdrop-blur-md rounded-xl text-center"
        style={{
          border: `2px solid ${path.colorHex}`,
          boxShadow: `0 0 40px ${path.colorHex}20`
        }}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="text-6xl mb-4"
        >
          ✨
        </motion.div>
        <h3 className="text-2xl font-bold text-white mb-4">Thank you!</h3>
        <p className="text-white/80 mb-6">
          We'll be in touch within 24 hours to discuss your {path.title.toLowerCase()}.
        </p>
        <button
          onClick={onClose}
          className="px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-full hover:bg-white/30 transition-all"
        >
          Start Another Inquiry
        </button>
      </motion.div>
    );
  }

  // Contact info fields
  if (currentFieldIndex === 'contact') {
    return (
      <motion.div
        ref={formRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto p-8 bg-white/10 backdrop-blur-md rounded-xl"
        style={{
          border: `2px solid ${path.colorHex}`,
          boxShadow: `0 0 40px ${path.colorHex}20`
        }}
      >
        {/* Progress bar */}
        <div className="mb-6">
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              className="h-full rounded-full"
              style={{ backgroundColor: path.colorHex }}
            />
          </div>
        </div>

        <h3 className="text-xl text-white mb-6">Almost done! How can we reach you?</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-white/80 mb-2">Your Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-white/50"
              placeholder="Jane Smith"
            />
          </div>

          <div>
            <label className="block text-white/80 mb-2">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-white/50"
              placeholder="jane@company.com"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 font-semibold rounded-lg text-white transition-all duration-300"
            style={{
              backgroundColor: isSubmitting ? '#666' : path.colorHex,
              opacity: isSubmitting ? 0.5 : 1
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
          </button>
        </form>
      </motion.div>
    );
  }

  // Regular form fields
  return (
    <motion.div
      ref={formRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto p-8 bg-white/10 backdrop-blur-md rounded-xl"
      style={{
        border: `2px solid ${path.colorHex}`,
        boxShadow: `0 0 40px ${path.colorHex}20`
      }}
    >
      {/* Progress bar */}
      <div className="mb-6">
        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            className="h-full rounded-full"
            style={{ backgroundColor: path.colorHex }}
          />
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Previous answers */}
      {currentFieldIndex > 0 && (
        <div className="mb-6 space-y-3 max-h-40 overflow-y-auto">
          {Object.entries(formData).map(([key, value]) => {
            const field = path.formFields.find(f => f.id === key);
            return (
              <div key={key} className="text-sm">
                <div className="text-white/60">{field?.label}</div>
                <div className="text-white/80">{value}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Current field */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentFieldIndex}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          <label className="block text-xl text-white mb-4">
            {currentField.label}
          </label>

          <FormField
            field={currentField}
            onSubmit={handleFieldSubmit}
            pathColor={path.colorHex}
          />
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

// Individual form field component
const FormField = ({ field, onSubmit, pathColor }) => {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    // Focus input when field appears
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim() || !field.required) {
      onSubmit(value.trim());
      setValue('');
    }
  };

  switch (field.type) {
    case 'select':
      return (
        <form onSubmit={handleSubmit}>
          <select
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required={field.required}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white/50"
          >
            <option value="">Choose one...</option>
            {field.options.map(option => (
              <option key={option} value={option} className="bg-gray-900">
                {option}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="mt-4 px-6 py-2 rounded-full text-white transition-all duration-300 hover:opacity-90"
            style={{ backgroundColor: pathColor }}
          >
            Continue →
          </button>
        </form>
      );

    case 'textarea':
      return (
        <form onSubmit={handleSubmit}>
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required={field.required}
            placeholder={field.placeholder}
            rows={4}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-white/50 resize-none"
          />
          <button
            type="submit"
            className="mt-4 px-6 py-2 rounded-full text-white transition-all duration-300 hover:opacity-90"
            style={{ backgroundColor: pathColor }}
          >
            Continue →
          </button>
        </form>
      );

    default: // text, date, email, etc.
      return (
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type={field.type}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required={field.required}
            placeholder={field.placeholder}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-white/50"
          />
          <button
            type="submit"
            className="mt-4 px-6 py-2 rounded-full text-white transition-all duration-300 hover:opacity-90"
            style={{ backgroundColor: pathColor }}
          >
            Continue →
          </button>
        </form>
      );
  }
};

export default ConversationalForm;